import {
  Component,
  Element,
  h,
  Host,
  Method,
  Prop,
  Watch
} from '@stencil/core';

const FADE_TIME = 500;

@Component({
  tag: 'uui-popover',
  styleUrl: 'uui-popover.css',
  // So we want radio buttons to work as expected in forms, and to have
  // radio group exclusivity, so they are NOT added to the shadow DOM.
  shadow: false,
  // However, for their styles to be correctly scoped as if they were
  // in the shadow root, we set this flag (and add :host to the globals).
  scoped: true
})
export class UUIPopover {
  /**
   * Whether the popover should be opened
   */
  @Prop({ reflect: true, mutable: true }) opened = false;

  /**
   * The underlying HTML element of this popover.
   */
  @Element()
  el: HTMLElement;

  /**
   * Position of the popover in context to the trigger
   */
  @Prop() position: 'left' | 'middle' | 'right' = 'left';

  /**
   * Hide the arrow on the top of the popover
   */
  @Prop() hidearrow: boolean = false;

  /**
   * Tests if a mouse click event is outside the popover.
   * If this is set to undefined or null, it disables the
   * dismisson on outside click behavior and the popover must
   * be manually dismissed.
   */
  @Prop() isOutsideClick:
    | ((e: MouseEvent) => boolean)
    | undefined
    | null
    | string;

  /**
   * This toggles the "new" behavior of always rendering the
   * popover on top of all the other DOM elements. Undefined
   * by default so we don't change the old behavior.
   */
  @Prop({ reflect: true }) alwaysontop: boolean;

  /**
   * When only a single popover should be displayed, set all uui-popover
   * components to the same group and exclusivity will be managed for you.
   */
  @Prop({ reflect: true }) group: string | undefined;

  /**
   * Enable event propogation when the popover is clicked.
   * Note: this will prevent having multiple popovers opened at the same time.
   */
  @Prop({ reflect: true }) alloweventpropagation: boolean = false;

  /**
   * Specify the id of the element to insert the popover into.
   * By default this will be the body element. Only used when
   * alwaysontop is set to true
   */
  @Prop() popoverparentelementid: string = null;
  /**
   * Specify if the popover should display when a user hovers or clicks the trigger
   */
  @Prop({ reflect: true }) eventType: 'hover' | 'click' = 'click';

  /**
   * opens dialog
   */
  @Method()
  async open() {
    this.opened = true;
    if (this.alwaysontop) {
      if (this.group) {
        const otherItems = document.querySelectorAll(
          `[data-group="${this.group}"]`
        );
        let otherPopover: Node;
        for (otherPopover of otherItems) {
          if (otherPopover !== this.popover) {
            otherPopover?.parentNode?.removeChild(otherPopover);
          }
        }
      }
      this.repositionPopover();
    } else {
      if (this.eventType === 'click') {
        this.popover.style.visibility = 'visible';
      }
    }
  }

  @Watch('opened')
  onOpenedChange(newValue, oldValue) {
    if (newValue === oldValue) return;
    if (newValue) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * closes dialog
   */
  @Method()
  async close() {
    this.opened = false;
    if (this.alwaysontop && this.popover && this.eventType === 'click') {
      setTimeout(() => {
        this.popover?.parentElement?.removeChild(this.popover);
      }, FADE_TIME);
    } else {
      setTimeout(() => {
        if (this.eventType === 'click') {
          this.popover.style.visibility = 'hidden';
        }
      }, FADE_TIME);
    }
  }

  /**
   * Toggle between opened and closed.
   */
  @Method()
  async toggle() {
    if (this.opened) {
      this.close();
    } else {
      this.open();
    }
  }

  // Popover element; used for repositioning and DOM manipulation
  // in the always on top case.
  popover: HTMLElement;

  get trigger(): HTMLElement | undefined {
    const trigger = this.el.querySelector('[slot="trigger"]') as HTMLElement;
    return trigger;
  }

  defaultIsOutsideClick(e: PointerEvent) {
    if (!this.el) {
      return false;
    }
    let isChild = false;
    if (this.alwaysontop) {
      if (!this.popover) {
        return false;
      }
      const targetNode = e.target as Node;
      isChild =
        this.el === targetNode ||
        this.popover.contains(targetNode) ||
        this.el.contains(targetNode);
    } else {
      isChild = this.el.contains(e.target as Node);
    }
    return !isChild;
  }

  repositionPopover() {
    if (this.popover) {
      const popover = this.popover;
      if (!popover.parentElement) {
        if (this.popoverparentelementid) {
          const element = document.getElementById(this.popoverparentelementid);
          element.appendChild(popover);
        } else {
          document.body.appendChild(popover);
        }
      }

      const popoverBoundingRect = popover.getBoundingClientRect();
      // Recurse until popover has a height.
      // This ensures it has rendered so it can actually
      // be measured.
      if (popoverBoundingRect.height === 0) {
        return requestAnimationFrame(() => this.repositionPopover());
      }

      const trigger = this.trigger;
      const triggerBoundingRect = trigger?.getBoundingClientRect();

      if (!triggerBoundingRect) return;

      popover.style.top = `${
        window.scrollY + triggerBoundingRect.y + triggerBoundingRect.height
      }px`;

      const triggerLeft = window.scrollX + triggerBoundingRect.x;

      switch (this.position) {
        case 'right': {
          popover.style.left = `${
            triggerLeft - popoverBoundingRect.width + triggerBoundingRect.width
          }px`;
          break;
        }
        case 'middle': {
          const triggerMiddle = triggerLeft + triggerBoundingRect.width / 2;
          const halfPopover = popoverBoundingRect.width / 2;
          const popoverLeft = triggerMiddle - halfPopover;
          popover.style.left = `${popoverLeft}px`;
          break;
        }
        default: {
          popover.style.left = `${triggerLeft}px`;
          break;
        }
      }
    }
  }

  captureStyles() {
    // Copy my styles to the document
    if (!document.head.querySelector('#uui-popover-style')) {
      const popoverStyle = this.el?.parentNode?.querySelector('style');
      if (popoverStyle) {
        popoverStyle.id = 'uui-popover-style';
        document.head.appendChild(popoverStyle);
      }
    }
  }

  componentDidRender() {
    this.captureStyles();
  }

  componentDidLoad(): void {
    if (this.isOutsideClick === undefined) {
      this.isOutsideClick = this.defaultIsOutsideClick.bind(this);
    }

    // always close the popover when a click outside of the element occurs
    if (this.isOutsideClick && typeof this.isOutsideClick !== 'string') {
      this.el.addEventListener('click', (e: PointerEvent) => {
        // These are definitely not outside the component so cancel the event
        !this.alloweventpropagation && e.stopPropagation();
      });

      document.addEventListener('click', (event: MouseEvent) => {
        const outsideClick =
          typeof this.isOutsideClick !== 'string'
            ? this.isOutsideClick(event)
            : false;
        if (outsideClick) {
          this.close();
        }
      });
    }

    const container = this.el.querySelector('.container');
    const popover = container?.querySelector('.popover') as HTMLElement;

    if (popover) {
      this.popover = popover;
      this.popover.style.position = 'absolute';

      if (this.alwaysontop) {
        container.removeChild(popover);
        if (this.group) {
          popover.setAttribute(`data-group`, this.group);
        }
      }
    }
    const trigger = this.trigger;

    if (this.eventType === 'hover') {
      trigger?.addEventListener('mouseover', () => {
        this.open();
      });

      trigger?.addEventListener('mouseout', () => {
        this.close();
      });
    } else {
      trigger?.addEventListener('click', () => {
        this.toggle();
      });
    }

    if (this.alwaysontop) {
      if (this.opened) {
        this.open();
      }
    } else {
      const positionPopover = () => {
        const popoverBoundingRect = popover.getBoundingClientRect();
        const triggerBoundingRect = trigger?.getBoundingClientRect();

        // Recurse until popover has a height.
        // This ensures it has rendered so it can actually
        // be measured.
        if (popoverBoundingRect.height === 0) {
          return requestAnimationFrame(positionPopover);
        }

        popover.style.top = '0';

        if (this.position === 'right' && triggerBoundingRect) {
          popover.style.left = `-${
            popoverBoundingRect.width - triggerBoundingRect.width
          }px`;
        }

        if (this.position === 'middle' && triggerBoundingRect) {
          popover.style.left = `-${
            popoverBoundingRect.width / 2 - triggerBoundingRect.width / 2
          }px`;
        }
      };
      positionPopover();
    }
  }

  render() {
    let baseClasses = `popover ${this.position}`;
    baseClasses += this.hidearrow ? 'hide-arrow' : '';
    const classes = this.opened
      ? `popover ${baseClasses} show`
      : `popover ${baseClasses} hide`;
    return (
      <Host>
        <slot name="trigger"></slot>
        <div class="container">
          <div class={classes}>
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
